/**
 * resumeTrace — o rastro DURÁVEL da retomada (BUG-196).
 *
 * ── Por que isto existe ─────────────────────────────────────────────────────
 * Três rodadas de fix de retomada (BUG-177, 179, 181/182) foram ao ar e o
 * relato de device continuou — e cada rodada foi diagnosticada NO ESCURO,
 * porque o `FirestoreResumeWatcher` só falava com `console.warn`, que num
 * TestFlight não existe para ninguém. O bootTrace resolve isso para o BOOT,
 * mas ele é memória por carga de página: um reload de recuperação apaga
 * exatamente o rastro que explicaria o reload.
 *
 * Este módulo é o irmão durável: um ring buffer em `localStorage` que
 * sobrevive ao reload e ao kill do app. Cada evento também é espelhado no
 * bootTrace da carga atual (o painel de toque longo mostra ao vivo), e o
 * rastro da carga ANTERIOR é despejado no boot seguinte — no painel e, quando
 * houve anormalidade, no Cloud Logging via `captureException` (a callable usa
 * `fetch`, que não passa pela fila do Firestore).
 *
 * ── Decisões ────────────────────────────────────────────────────────────────
 * 1. **`localStorage`, não `sessionStorage`.** O ponto é sobreviver ao reload
 *    E ao kill — o relato chega horas depois do episódio.
 * 2. **Nunca lança.** Storage indisponível degrada para "só bootTrace"; o
 *    rastro é observação, jamais política (doutrina do bootTrace).
 * 3. **Sem dado pessoal.** Vira print e vai por WhatsApp: step + detail curto.
 */

import { bootMark } from "../boot/bootTrace";
import { captureException } from "../monitoring";

export const RESUME_TRACE_KEY = "livra:resume_trace";
// 7ª fatia do BUG-197: `hidden`/`visible` somam 2 linhas por episódio e o
// pipeline inteiro passa de ~15. Com 40, cabiam 3 episódios — e no relato do
// build 89 os três que sobreviveram eram os SÃOS; o congelado era o 4º. O
// diário tem de guardar mais episódios do que a pessoa consegue testar
// antes de mandar o print.
const MAX_ENTRIES = 80;

/** Eventos que denunciam episódio anormal — são os que valem telemetria. */
const ABNORMAL_STEPS = new Set([
  "probe-dead",
  "paint-frozen",
  "cycle-hung",
  "verify-dead",
  "listener-dead",
  // Sentinela NATIVA (BUG-197 3ª fatia): "recovered" também é anormal — houve
  // congelamento real que o re-attach curou, e é a manchete da calibração.
  "native-paint-dead",
  "native-paint-hung",
  "native-paint-recovered",
  // 5ª fatia: a amostra da JANELA (o que o vidro compõe) discordando do
  // compositor é o dado que decide esta família — e "dead" é o reload.
  "native-screen-uniform",
  "native-screen-dead",
  "native-screen-recovered",
  "native-screen-late-uniform",
  // 4ª fatia: os canais que verify-ok não cobre.
  "verify-write-dead",
  "img-host-hung",
  "img-host-failed",
  "fn-host-hung",
  "fn-host-failed",
  "reload",
  "no-slot",
  // 7ª fatia: o JS que NÃO acordou. `visible-sem-wake` é derivado no flush
  // (o evento de visibilidade chegou ao JS e o watcher nunca chegou a rodar
  // — a thread travou dentro do assentamento de 1,2s); `native-js-hung` é o
  // ping de vida do AppDelegate sem resposta, replicado no boot seguinte.
  // `hidden-sem-visible` NÃO está aqui de propósito: um swipe-kill normal
  // no app switcher produz exatamente esse par, e telemetria por ele seria
  // ruído — quem o separa do congelamento é o `active` do ring nativo.
  "visible-sem-wake",
  "native-js-hung",
  // 2ª metade da 7ª fatia: o AppDelegate recriou a WebView porque o JS parou
  // de responder ao batimento. É a manchete do episódio — e a prova de que a
  // cura rodou.
  "native-webview-recreated",
  // 3ª rodada de 02/09: a thread respondeu ao ping mas os timers do
  // documento não batem há ≥4s (ou o documento se acha oculto com o app
  // ativo). É o estado do 12:34:01 do 89 e do QA do 91.
  "native-js-stale",
]);

/**
 * Batimento do JS (BUG-197, 7ª fatia, 2ª metade).
 *
 * O diário do 90 mostrou o JS respondendo 59ms depois de acordar e morrendo
 * ~2s depois, sem deixar linha — porque nenhuma linha estava programada para
 * aquele instante. Uma escrita síncrona por segundo em `localStorage` deixa
 * o carimbo do ÚLTIMO segundo em que a thread rodou; o boot seguinte o
 * despeja como `js-last-beat`, e cruzado com os eventos do ring nativo diz
 * COM PRECISÃO DE 1s quando o JS parou. Custo: um `setItem` de 13 bytes por
 * segundo, só com a página visível.
 */
export const JS_BEAT_KEY = "livra:js_beat";

export function recordJsBeat(now: number = Date.now()): void {
  // O espelho em memória é o que o ping NATIVO lê (`__luraLastBeat`): a
  // idade dele diz se os timers do documento estão rodando — a thread pode
  // responder a `evaluateJavaScript` com os timers parados (89, 12:34:01).
  // Atualizado SEMPRE que o interval bate, visível ou não: é o batimento do
  // timer, não da tela.
  (window as unknown as Record<string, unknown>).__luraLastBeat = now;
  try {
    localStorage.setItem(JS_BEAT_KEY, String(now));
  } catch {
    // storage bloqueado: sem batimento, sem exceção
  }
}

function readLastBeat(storage: Storage | null): number | null {
  try {
    const raw = storage?.getItem(JS_BEAT_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/**
 * Diagnóstico de AUSÊNCIA (BUG-197, 7ª fatia).
 *
 * Seis fatias de diário e nenhuma perguntou o que NÃO estava no log. No 89,
 * os três episódios gravados voltaram sãos e o congelado (Home) não deixou
 * uma linha — porque `wake` só é escrito 1,2s DEPOIS do evento, num
 * `setTimeout`, e uma thread de JS presa não executa timer nenhum. O
 * watcher passou a gravar `hidden` (com a rota) e `visible` de forma
 * síncrona, no próprio handler do evento; esta função lê o que veio depois
 * do último `hidden`:
 *   · nada → `hidden-sem-visible` (o evento de volta nunca chegou ao JS — OU
 *     a pessoa matou o app no background; só o ring nativo separa os dois);
 *   · `visible` sem `wake` → `visible-sem-wake` (o JS acordou e morreu
 *     dentro do assentamento — o congelamento aconteceu no nosso código,
 *     disparado pela retomada).
 * Um marcador já gravado depois do `hidden` encerra o diagnóstico: o flush
 * seguinte não o repete.
 */
export type OrphanResume = "hidden-sem-visible" | "visible-sem-wake";
const ORPHAN_STEPS: ReadonlySet<string> = new Set(["hidden-sem-visible", "visible-sem-wake"]);

export function detectOrphanResume(
  entries: ReadonlyArray<{ step: string }>,
): OrphanResume | null {
  let lastHidden = -1;
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i].step === "hidden") {
      lastHidden = i;
      break;
    }
  }
  if (lastHidden === -1) return null;
  const after = entries.slice(lastHidden + 1);
  if (after.some((e) => ORPHAN_STEPS.has(e.step))) return null;
  if (!after.some((e) => e.step === "visible")) return "hidden-sem-visible";
  if (!after.some((e) => e.step === "wake")) return "visible-sem-wake";
  return null;
}

interface ResumeTraceEntry {
  /** Epoch ms — o rastro cruza cargas de página, então o eixo é o relógio. */
  t: number;
  step: string;
  detail?: string;
}

interface ResumeTraceDoc {
  v: 1;
  /** `true` depois de o episódio anormal mais recente ter ido pra telemetria. */
  flushed: boolean;
  entries: ResumeTraceEntry[];
}

function localStorageOrNull(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Cópia VIVA do ring desta carga (17/09, rodada de performance).
 *
 * `traceResume` fazia `getItem` + `JSON.parse` + `stringify` + `setItem` a
 * cada linha — e uma retomada emite 15+ linhas em sequência, todas síncronas
 * na thread principal, dentro da janela em que o batimento nativo decide se o
 * JS travou. Ler o storage uma vez e manter o documento em memória corta a
 * leitura e o parse; a ESCRITA síncrona por linha fica, porque ela é o que
 * sobrevive quando o JS morre no meio (é a razão de o diário existir).
 */
let liveDoc: ResumeTraceDoc | null = null;

function readDoc(storage: Storage | null): ResumeTraceDoc {
  const empty: ResumeTraceDoc = { v: 1, flushed: true, entries: [] };
  if (!storage) return empty;
  try {
    const raw = storage.getItem(RESUME_TRACE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<ResumeTraceDoc> | null;
    if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.entries)) return empty;
    return { v: 1, flushed: parsed.flushed !== false, entries: parsed.entries };
  } catch {
    return empty;
  }
}

function writeDoc(storage: Storage | null, doc: ResumeTraceDoc): void {
  if (!storage) return;
  try {
    storage.setItem(RESUME_TRACE_KEY, JSON.stringify(doc));
  } catch {
    // Quota/privado: o rastro desta carga vive só no bootTrace.
  }
}

/**
 * Registra um evento de retomada. Espelha no bootTrace (painel ao vivo) e
 * persiste no ring durável. Evento anormal desarma o `flushed`, para o boot
 * seguinte mandar o episódio pra telemetria.
 */
export function traceResume(step: string, detail?: string): void {
  const abnormal = ABNORMAL_STEPS.has(step);
  bootMark(`resume:${step}`, abnormal ? "fail" : "info", detail);

  const storage = localStorageOrNull();
  const doc = liveDoc ?? (liveDoc = readDoc(storage));
  doc.entries.push({ t: Date.now(), step, ...(detail ? { detail } : {}) });
  while (doc.entries.length > MAX_ENTRIES) doc.entries.shift();
  if (abnormal) doc.flushed = false;
  writeDoc(storage, doc);
}

/** Só para testes: esquece a cópia viva (o storage é a verdade entre casos). */
export function __resetResumeTraceForTests(): void {
  liveDoc = null;
}

/**
 * Despeja o rastro da(s) carga(s) anterior(es): tudo entra no bootTrace da
 * carga atual (o painel conta a história do reload que acabou de acontecer) e,
 * se houve anormalidade não enviada, UM `captureException` leva o episódio ao
 * Cloud Logging. Chamar uma vez, no mount do watcher.
 */
export function flushResumeTrace(): void {
  const storage = localStorageOrNull();
  // O último segundo em que o JS da carga ANTERIOR rodou — lido ANTES de o
  // batimento desta carga sobrescrever a chave (o watcher só o inicia depois
  // deste flush).
  const lastBeat = readLastBeat(storage);
  if (lastBeat !== null) {
    bootMark("resume(prev):js-last-beat", "info", new Date(lastBeat).toISOString());
  }
  // O flush lê o storage de verdade (é o ring da carga ANTERIOR) e a partir
  // daqui este documento é a cópia viva — `traceResume` escreve nele.
  const doc = readDoc(storage);
  liveDoc = doc;
  if (doc.entries.length === 0) return;

  // O episódio que não deixou rastro vira UMA linha, com a rota do `hidden`
  // que o abriu — é a única forma de o diário dizer "a Home congelou" quando
  // a Home congelada não escreveu nada. Gravado no ring (sobrevive ao
  // próximo reload) antes do despejo, para sair no painel junto do resto.
  const orphan = detectOrphanResume(doc.entries);
  if (orphan) {
    const hidden = [...doc.entries].reverse().find((e) => e.step === "hidden");
    doc.entries.push({ t: Date.now(), step: orphan, ...(hidden?.detail ? { detail: hidden.detail } : {}) });
    while (doc.entries.length > MAX_ENTRIES) doc.entries.shift();
    if (ABNORMAL_STEPS.has(orphan)) doc.flushed = false;
    writeDoc(storage, doc);
  }

  for (const e of doc.entries) {
    bootMark(
      `resume(prev):${e.step}`,
      ABNORMAL_STEPS.has(e.step) ? "fail" : "info",
      `${new Date(e.t).toISOString()}${e.detail ? ` — ${e.detail}` : ""}`,
    );
  }

  if (!doc.flushed) {
    captureException(new Error("firestore_resume_recovery"), {
      kind: "resume-trace",
      trace: doc.entries.map((e) => `${new Date(e.t).toISOString()} ${e.step}${e.detail ? ` ${e.detail}` : ""}`),
    });
    doc.flushed = true;
    writeDoc(storage, doc);
  }
}
